/**
 * Event landing-page template — 09-workshop ("Strategy Workshop").
 *
 * Distinct CraftJS home-page document for the `workshop` theme. Cloned from the
 * proven `webinar` template (same structure: temp.ts factories, section node
 * factories, scaffold + sections, plus Appbar/Footer meta sections).
 *
 * Visual style — "minimal-studio": minimalist, generous whitespace, pure
 * ink-on-white palette (WS6: recoloured from the old greys to the shared
 * `minimal` design direction), Work Sans headings over Inter body, subtle
 * rounding (rounded-md). Copy lifted from
 *   TEMPLATES - LANDINPAGES/09-workshop/sections/*.html (CRAFT_PROPS_HINT).
 *
 * Convention (mirrors webinar.ts):
 *  - Section-level marketing copy → td() keys (unique `wsh*` prefix).
 *  - Repetitive structured child data (agenda rows, speakers, FAQ, …) also
 *    go through td() keys (Wave 3b i18n conversion) — the *_DATA tables are
 *    functions of td so every user-visible row string localizes.
 *  - Image-dependent sections (Speaker/Gallery/Testimonials) default hidden and
 *    repoint to seeded workshop/* graphics so they resolve when enabled.
 */
import { defaultRegistrationConsents, toCheckboxItems } from "@/lib/website/registrationConsents";
import {
  A,
  enc,
  pv,
  tc,
  btn,
  fi,
  acceptanceFi,
  scaffold,
  emptyFieldsGroup,
  PAD,
  DIST_CARD,
  BTN_FORM_SP,
  SEC_MARGIN,
  ACCEPTANCE_VISIBILITY_FILTER,
  type N,
  type TranslationFn,
} from "../temp";
import { EASE, decorLayer, fadeUpKeyframes, fontFaces, heroShell, motionGate, revealBlock } from "./premium-css";
import { direction } from "@/lib/designDirections/directions";
import { type WebsiteThemeStylePreset } from "@/types/WebsiteTheme";
import {
  SAMPLE_SPEAKER_IMAGES,
  SAMPLE_SPONSOR_LOGOS,
} from "@/lib/website-builder/sampleContent";

// Workshop-specific asset namespace (seeded under <CDN>/workshop/…)
const W = `${A}/shared/templates/09-workshop`;

// WS6: workshop recolors from its old grey palette to the shared `minimal`
// design direction (registry canon). Alias names kept so usages don't churn.
const ACCENT = direction("minimal").primary; // ink studio accent (#111111)
const INK = direction("minimal").text; // headings (#111111)
const MUTE = direction("minimal").accent; // muted body (#6B6B6B)
const SURFACE = direction("minimal").background; // page surface (#FFFFFF)
const DARK = "#1a1a1a"; // dark section surface
const ON_DARK = "#d4d4d4"; // body text on dark
const ON_DARK_ACCENT = "#a3a3a3"; // eyebrow on dark
// Canon `minimal` faces: Work Sans headings, Inter body (self-hosted below).
const HEAD = "'Work Sans', Inter, 'Helvetica Neue', Arial, sans-serif";
const SANS = "Inter, 'Helvetica Neue', Arial, sans-serif";

// Speaker/testimonial faces come from the shared sample rotation
// (lib/website-builder/sampleContent); the template's own `workshop/*` art
// stays what it is: heroes, details and gallery photography.
const LOGO = `${A}/shared/logo.svg`;

// ── Child data (localized via td — Wave 3b) ────────────────────────────

const NUMBERS_DATA = (td: TranslationFn) => [
  { num: "<b>14h</b>", desc: td("wshNum1Desc") },
  { num: "<b>24</b>", desc: td("wshNum2Desc") },
  { num: "<b>7</b>", desc: td("wshNum3Desc") },
  { num: "<b>3</b>", desc: td("wshNum4Desc") },
];

const AGENDA_DATA = (td: TranslationFn) => [
  { time: `<b>${td("wshAgendaRow1Time")}</b>`, title: td("wshAgendaRow1Title"), speaker: td("wshAgendaRow1Who"), desc: td("wshAgendaRow1Desc") },
  { time: `<b>${td("wshAgendaRow2Time")}</b>`, title: td("wshAgendaRow2Title"), speaker: td("wshAgendaRow2Who"), desc: td("wshAgendaRow2Desc") },
  { time: `<b>${td("wshAgendaRow3Time")}</b>`, title: td("wshAgendaRow3Title"), speaker: td("wshAgendaRow3Who"), desc: td("wshAgendaRow3Desc") },
  { time: `<b>${td("wshAgendaRow4Time")}</b>`, title: td("wshAgendaRow4Title"), speaker: td("wshAgendaRow4Who"), desc: td("wshAgendaRow4Desc") },
  { time: `<b>${td("wshAgendaRow5Time")}</b>`, title: td("wshAgendaRow5Title"), speaker: td("wshAgendaRow5Who"), desc: td("wshAgendaRow5Desc") },
];

const SPEAKER_DATA = (td: TranslationFn) => [
  { name: td("wshSpeakerNameLead"), role: td("wshSpeaker1Role"), img: 0 },
  { name: td("wshSpeakerNameCo"), role: td("wshSpeaker2Role"), img: 1 },
  { name: "Dr. Rieke Lange", role: td("wshSpeaker3Role"), img: 2 },
  { name: td("wshSpeakerNameMentor"), role: td("wshSpeaker4Role"), img: 3 },
  { name: td("wshSpeakerNameCo"), role: td("wshSpeaker5Role"), img: 4 },
  { name: td("wshSpeakerNameMentor"), role: td("wshSpeaker6Role"), img: 5 },
];

const TESTIMONIAL_DATA = (td: TranslationFn) => [
  { quote: td("wshTesti1Quote"), name: "Dr. Helena Bischoff", role: td("wshTesti1Role"), img: 0 },
  { quote: td("wshTesti2Quote"), name: "Jakob Hartmann", role: td("wshTesti2Role"), img: 1 },
  { quote: td("wshTesti3Quote"), name: "Mira Olusegun", role: td("wshTesti3Role"), img: 2 },
];

const BLOG_DATA = (td: TranslationFn) => [
  { topic: `<b>${td("wshBlog1Topic")}</b>`, content: td("wshBlog1Content") },
  { topic: `<b>${td("wshBlog2Topic")}</b>`, content: td("wshBlog2Content") },
  { topic: `<b>${td("wshBlog3Topic")}</b>`, content: td("wshBlog3Content") },
];

const FAQ_DATA = (td: TranslationFn) => [
  { q: `<b>${td("wshFaq1Question")}</b>`, a: td("wshFaq1Answer") },
  { q: `<b>${td("wshFaq2Question")}</b>`, a: td("wshFaq2Answer") },
  { q: `<b>${td("wshFaq3Question")}</b>`, a: td("wshFaq3Answer") },
  { q: `<b>${td("wshFaq4Question")}</b>`, a: td("wshFaq4Answer") },
  { q: `<b>${td("wshFaq5Question")}</b>`, a: td("wshFaq5Answer") },
  { q: `<b>${td("wshFaq6Question")}</b>`, a: td("wshFaq6Answer") },
];

// ── Workshop-specific child factories ─────────────────────────────────

function numbersChild(parent: string, i: number, td: TranslationFn): N {
  const d = NUMBERS_DATA(td)[i];
  return {
    type: { resolvedName: "NumbersChild" }, isCanvas: false,
    props: {
      isVisible: true,
      isVisibleNumber: true, textNumber: d.num, fontSizeNumber: "text-4xl", textAlignNumber: "center",
      isVisibleDescription: true, textDescription: d.desc, fontSizeDescription: "text-sm", textAlignDescription: "center",
    },
    displayName: "NumbersChild", custom: { icon: "IconNumber" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function agendaChild(parent: string, i: number, td: TranslationFn): N {
  const d = AGENDA_DATA(td)[i];
  return {
    type: { resolvedName: "AgendaChild" }, isCanvas: false,
    props: {
      isVisibleText1: true, textText1: d.time, fontSizeText1: "text-sm", textAlignText1: "left",
      isVisibleText2: true, textText2: d.title, fontSizeText2: "text-sm", textAlignText2: "left",
      isVisibleText3: true, textText3: d.speaker, fontSizeText3: "text-sm", textAlignText3: "left",
      isVisibleText: true, textText: d.desc, fontSizeText: "text-sm", textAlignText: "left", border: true,
    },
    displayName: "AgendaChild", custom: { icon: "IconCalendar" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function speakerChild(parent: string, i: number, td: TranslationFn): N {
  const d = SPEAKER_DATA(td)[i];
  return {
    type: { resolvedName: "SpeakerChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPEAKER_IMAGES[d.img % SAMPLE_SPEAKER_IMAGES.length], imageBottomBackground: "transparent",
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left",
      icons: [{ icon: "IconBrandLinkedin" }, { icon: "IconMail" }], isVisibleIcon: true,
    },
    displayName: "SpeakerChild", custom: { icon: "IconMicrophone" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function testimonialChild(parent: string, i: number, td: TranslationFn): N {
  const d = TESTIMONIAL_DATA(td)[i];
  return {
    type: { resolvedName: "TestimonialsChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPONSOR_LOGOS[i % SAMPLE_SPONSOR_LOGOS.length].src,
      imageUrlBottom: SAMPLE_SPEAKER_IMAGES[d.img % SAMPLE_SPEAKER_IMAGES.length],
      isVisibleImageTop: false, imageBottomBackground: "transparent", isVisibleImageBottom: false,
      isVisibleContent: true, textContent: d.quote, fontSizeContent: "text-md", textAlignContent: "left", fontColorContent: ON_DARK,
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left", fontColorName: "#ffffff",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left", fontColorOccupation: ON_DARK,
    },
    displayName: "TestimonialsChild", custom: { icon: "IconThumbUp" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function blogChild(parent: string, i: number, td: TranslationFn): N {
  const d = BLOG_DATA(td)[i];
  return {
    type: { resolvedName: "BlogChild" }, isCanvas: false,
    props: {
      iconAlignment: "left", isVisible: true,
      isVisibleTopic: true, textTopic: d.topic, fontSizeTopic: "text-lg", textAlignTopic: "left",
      isVisibleContent: true, textContent: d.content, fontSizeContent: "text-md", textAlignContent: "left",
      isVisibleButton: false, size: "small", text: td("learnMore"), underline: false, border: true,
    },
    displayName: "BlogChild", custom: { icon: "IconArticle" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function faqChild(parent: string, i: number, td: TranslationFn): N {
  const d = FAQ_DATA(td)[i];
  return {
    type: { resolvedName: "FaQChild" }, isCanvas: false,
    props: {
      isVisibleQuestion: true, textQuestion: d.q, fontSizeQuestion: "text-sm", textAlignQuestion: "left",
      isVisibleAnswer: true, textAnswer: d.a, fontSizeAnswer: "text-sm", textAlignAnswer: "left", border: true,
    },
    displayName: "FaQChild", custom: { icon: "IconMessageQuestion" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

const GALLERY_IMG = [
  `${W}/hero-einladung.jpg`, `${W}/hero-save-the-date.jpg`, `${W}/detail-notebook.jpg`,
  `${W}/detail-roadmap.jpg`, `${W}/hero-dokumente.jpg`, `${W}/hero-treatment.jpg`,
];

function galleryImage(parent: string, i: number): N {
  return {
    type: { resolvedName: "ImageChild" }, isCanvas: false,
    props: { src: GALLERY_IMG[i % GALLERY_IMG.length], isVisible: true, aspectRatio: "aspect-video", displayName: "ImageChild", width: "w-full", canDuplicate: true },
    displayName: "ImageChild", custom: { icon: "IconSection" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

// ── Template ──────────────────────────────────────────────────────────

export const workshop = (td: TranslationFn): string => {
  const B = "wsh_body";

  // Top-level section ids (body order)
  const HERO = "wsh_hero";
  const NUMBERS = "wsh_numbers";
  const AGENDA = "wsh_agenda";
  const SPEAKER = "wsh_speaker";
  const TEXT = "wsh_text";
  const IMAGE = "wsh_image";
  const COUNTDOWN = "wsh_countdown";
  const GALLERY = "wsh_gallery";
  const VIDEO = "wsh_video";
  const TESTI = "wsh_testimonials";
  const BLOG = "wsh_blog";
  const FAQ = "wsh_faq";
  const MAP = "wsh_map";
  const DI = "wsh_datainput";
  const CONTACT = "wsh_contact";

  return enc({
    ...scaffold(
      { ab: "wsh_ab", abs: "wsh_abs", body: B, ft: "wsh_ft", fts: "wsh_fts" },
      [HERO, NUMBERS, DI, AGENDA, SPEAKER, TEXT, IMAGE, COUNTDOWN, GALLERY, VIDEO, TESTI, BLOG, FAQ, MAP, CONTACT],
      false, { background: "bg-white", padding: "p-4", borderRadius: "rounded-md" },
    ),

    // ── Hero (image right, dark text on warm surface) ──
    [HERO]: {
      type: { resolvedName: "Hero" }, isCanvas: false,
      props: {
        sectionHeight: 88, contentAlignment: "center", imageLayout: "right", layoutWidth: "50%", inversed: true,
        padding: PAD, distButtons: "gap-3 wb-md:gap-4", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", buttonAlignment: "flex-row", isVisible: true,
        isVisibleHeadline: true, textHeadline: td("wshHeroEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left", headlineColor: ACCENT,
        isVisibleHeader: true, textHeader: td("wshHeroHeader"), fontSizeHeader: "text-6xl", textAlignHeader: "left", headerColor: INK,
        isVisibleText: true, textText: td("wshHeroText"), fontSizeText: "text-lg", textAlignText: "left", textColor: MUTE,
        secondaryButtonSize: "medium", secondaryButtonText: td("wshHeroSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("wshHeroPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
        backgroundType: "color", imageRounded: false, backgroundOverlayColor: "transparent",
        imageSrc: `${W}/hero-einladung.jpg`, backgroundColor: SURFACE,
      },
      displayName: "Hero", custom: { icon: "IconSlideshow", preview: pv("Hero-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "wsh_hero_eb", header: "wsh_hero_hd", text: "wsh_hero_tx", buttonOne: "wsh_hero_b1", buttonTwo: "wsh_hero_b2" },
    },
    wsh_hero_eb: tc(HERO, { isVisible: true, text: td("wshHeroEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    wsh_hero_hd: tc(HERO, { isVisible: true, text: td("wshHeroHeader"), fontSize: "text-6xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    wsh_hero_tx: tc(HERO, { isVisible: true, text: td("wshHeroText"), fontSize: "text-lg", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    wsh_hero_b1: btn(HERO, { type: "secondary", text: td("wshHeroSecondaryButton"), displayName: "buttonOne" }),
    wsh_hero_b2: btn(HERO, { type: "primary", linkType: "internal", linkUrl: "#" + DI, text: td("wshHeroPrimaryButton"), displayName: "buttonTwo" }),

    // ── Numbers ──
    [NUMBERS]: {
      type: { resolvedName: "Numbers" }, isCanvas: true,
      props: {
        sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN, gridColumns: "4",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: true, textHeadline: td("wshNumbersEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wshNumbersHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("wshNumbersText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Numbers", custom: { icon: "Icon123", preview: pv("NumbersPreview"), allowedComponents: ["NumbersChild"] },
      parent: B, hidden: false, nodes: ["wsh_num1", "wsh_num2", "wsh_num3", "wsh_num4"],
      linkedNodes: { headline: "wsh_num_eb", header: "wsh_num_hd", text: "wsh_num_tx" },
    },
    wsh_num_eb: tc(NUMBERS, { isVisible: true, text: td("wshNumbersEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    wsh_num_hd: tc(NUMBERS, { isVisible: true, text: td("wshNumbersHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    wsh_num_tx: tc(NUMBERS, { isVisible: true, text: td("wshNumbersText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    wsh_num1: numbersChild(NUMBERS, 0, td),
    wsh_num2: numbersChild(NUMBERS, 1, td),
    wsh_num3: numbersChild(NUMBERS, 2, td),
    wsh_num4: numbersChild(NUMBERS, 3, td),

    // ── Agenda ──
    [AGENDA]: {
      type: { resolvedName: "Agenda" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: true, textHeadline: td("wshAgendaEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wshAgendaHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("wshAgendaText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Agenda", custom: { icon: "IconListCheck", preview: pv("AgendaPreview"), allowedComponents: ["AgendaChild"] },
      parent: B, hidden: false, nodes: ["wsh_ag1", "wsh_ag2", "wsh_ag3", "wsh_ag4", "wsh_ag5"],
      linkedNodes: { headline: "wsh_ag_eb", header: "wsh_ag_hd", text: "wsh_ag_tx" },
    },
    wsh_ag_eb: tc(AGENDA, { isVisible: true, text: td("wshAgendaEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    wsh_ag_hd: tc(AGENDA, { isVisible: true, text: td("wshAgendaHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    wsh_ag_tx: tc(AGENDA, { isVisible: true, text: td("wshAgendaText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    wsh_ag1: agendaChild(AGENDA, 0, td),
    wsh_ag2: agendaChild(AGENDA, 1, td),
    wsh_ag3: agendaChild(AGENDA, 2, td),
    wsh_ag4: agendaChild(AGENDA, 3, td),
    wsh_ag5: agendaChild(AGENDA, 4, td),

    // ── Speaker (hidden by default — no portrait library) ──
    [SPEAKER]: {
      type: { resolvedName: "Speaker" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextSpeakers: "gap-8 wb-md:gap-12", distSpeakers: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: true, textHeadline: td("wshSpeakerEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wshSpeakerHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("wshSpeakerText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Speaker", custom: { icon: "IconUserStar", preview: pv("Speaker-preview"), allowedComponents: ["SpeakerChild"] },
      parent: B, hidden: false, nodes: ["wsh_sp1", "wsh_sp2", "wsh_sp3", "wsh_sp4", "wsh_sp5", "wsh_sp6"],
      linkedNodes: { headline: "wsh_sp_eb", header: "wsh_sp_hd", text: "wsh_sp_tx" },
    },
    wsh_sp_eb: tc(SPEAKER, { isVisible: true, text: td("wshSpeakerEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    wsh_sp_hd: tc(SPEAKER, { isVisible: true, text: td("wshSpeakerHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    wsh_sp_tx: tc(SPEAKER, { isVisible: true, text: td("wshSpeakerText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    wsh_sp1: speakerChild(SPEAKER, 0, td),
    wsh_sp2: speakerChild(SPEAKER, 1, td),
    wsh_sp3: speakerChild(SPEAKER, 2, td),
    wsh_sp4: speakerChild(SPEAKER, 3, td),
    wsh_sp5: speakerChild(SPEAKER, 4, td),
    wsh_sp6: speakerChild(SPEAKER, 5, td),

    // ── Text (editorial note from instructor) ──
    [TEXT]: {
      type: { resolvedName: "TextSection" }, isCanvas: false,
      props: {
        buttonAlign: "center", padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: true, textHeadline: td("wshTextEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wshTextHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("wshTextBody"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("wshTextSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false, secondaryButtonAlign: "center",
        primaryButtonSize: "medium", primaryButtonText: td("wshTextPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false, primaryButtonAlign: "center",
      },
      displayName: "Text", custom: { icon: "IconAlignJustified", preview: pv("textPreview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "wsh_tx_eb", header: "wsh_tx_hd", text: "wsh_tx_tx", buttonOne: "wsh_tx_b1", buttonTwo: "wsh_tx_b2" },
    },
    wsh_tx_eb: tc(TEXT, { isVisible: true, text: td("wshTextEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    wsh_tx_hd: tc(TEXT, { isVisible: true, text: td("wshTextHeader"), fontSize: "text-4xl", textAlign: "center", displayName: "header", header: true }),
    wsh_tx_tx: tc(TEXT, { isVisible: true, text: td("wshTextBody"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    wsh_tx_b1: btn(TEXT, { type: "secondary", text: td("wshTextSecondaryButton"), displayName: "buttonOne" }),
    wsh_tx_b2: btn(TEXT, { type: "primary", text: td("wshTextPrimaryButton"), displayName: "buttonTwo" }),

    // ── Image (material detail, image left / text right) ──
    [IMAGE]: {
      type: { resolvedName: "Image" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, inversed: true,
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true,
        isVisibleHeadline: true, textHeadline: td("wshImageEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("wshImageHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("wshImageText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("wshImageButton"), isVisibleButton: true, buttonAlign: "left",
        buttonBorder: true, buttonUnderline: false,
        imageRounded: "rounded-md", layoutWidth: "50%",
        isVisibleImage: true, imageSrc: `${W}/detail-notebook.jpg`, imageAspectRatio: "aspect-[4/5]",
      },
      displayName: "Image", custom: { icon: "IconSection", preview: pv("Image-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "wsh_im_eb", header: "wsh_im_hd", text: "wsh_im_tx", button: "wsh_im_btn", image: "wsh_im_img" },
    },
    wsh_im_eb: tc(IMAGE, { isVisible: true, text: td("wshImageEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    wsh_im_hd: tc(IMAGE, { isVisible: true, text: td("wshImageHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    wsh_im_tx: tc(IMAGE, { isVisible: true, text: td("wshImageText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    wsh_im_btn: btn(IMAGE, { text: td("wshImageButton"), displayName: "button" }),
    wsh_im_img: {
      type: { resolvedName: "ImageChild" }, isCanvas: false,
      props: { src: `${W}/detail-notebook.jpg`, isVisible: true, aspectRatio: "aspect-[4/5]", displayName: "ImageChild", width: "w-full wb-lg:w-1/2", canDuplicate: false },
      displayName: "ImageChild", custom: { icon: "IconSection" },
      parent: IMAGE, hidden: false, nodes: [], linkedNodes: {},
    },

    // ── Countdown ──
    [COUNTDOWN]: {
      type: { resolvedName: "Countdown" }, isCanvas: false,
      props: {
        sectionHeight: 40, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distButtonCountdown: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true, buttonAlign: "left",
        isVisibleHeadline: true, textHeadline: td("wshCountdownEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("wshCountdownHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("wshCountdownText"), fontSizeText: "text-base", textAlignText: "left",
        buttonSize: "medium", buttonText: td("wshCountdownButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
        isVisiblCountdown: true, countdownSizeDate: "text-5xl", countdownFontTypeDate: HEAD, countdownFontColorDate: INK,
        countdownSize: "text-base", countdownFontColor: MUTE, countdownBorderColor: "#e8e8e8",
      },
      displayName: "Countdown", custom: { icon: "IconAlarm", preview: pv("Countdown-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "wsh_cd_eb", header: "wsh_cd_hd", text: "wsh_cd_tx", button: "wsh_cd_btn" },
    },
    wsh_cd_eb: tc(COUNTDOWN, { isVisible: true, text: td("wshCountdownEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    wsh_cd_hd: tc(COUNTDOWN, { isVisible: true, text: td("wshCountdownHeader"), fontSize: "text-4xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    wsh_cd_tx: tc(COUNTDOWN, { isVisible: true, text: td("wshCountdownText"), fontSize: "text-base", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    wsh_cd_btn: btn(COUNTDOWN, { text: td("wshCountdownButton"), displayName: "ButtonChild" }),

    // ── Gallery (studio & setup) — hidden by default ──
    [GALLERY]: {
      type: { resolvedName: "Gallery" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", distButtonGallery: "gap-3 wb-md:gap-4", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, buttonAlign: "center", gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: true, textHeadline: td("wshGalleryEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wshGalleryHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("wshGalleryHeader"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("wshGallerySecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("wshGalleryPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
      },
      displayName: "Gallery", custom: { icon: "IconPhoto", preview: pv("Gallery-preview"), allowedComponents: ["ImageChild"] },
      parent: B, hidden: false,
      nodes: ["wsh_g1", "wsh_g2", "wsh_g3", "wsh_g4", "wsh_g5", "wsh_g6"],
      linkedNodes: { headline: "wsh_g_eb", header: "wsh_g_hd", text: "wsh_g_tx", secondary: "wsh_g_b2", primary: "wsh_g_b1" },
    },
    wsh_g_eb: tc(GALLERY, { isVisible: true, text: td("wshGalleryEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    wsh_g_hd: tc(GALLERY, { isVisible: true, text: td("wshGalleryHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    wsh_g_tx: tc(GALLERY, { isVisible: false, text: td("wshGalleryHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    wsh_g_b2: btn(GALLERY, { type: "secondary", text: td("wshGallerySecondaryButton"), displayName: "ButtonChild" }),
    wsh_g_b1: btn(GALLERY, { text: td("wshGalleryPrimaryButton"), displayName: "ButtonChild" }),
    wsh_g1: galleryImage(GALLERY, 0),
    wsh_g2: galleryImage(GALLERY, 1),
    wsh_g3: galleryImage(GALLERY, 2),
    wsh_g4: galleryImage(GALLERY, 3),
    wsh_g5: galleryImage(GALLERY, 4),
    wsh_g6: galleryImage(GALLERY, 5),

    // ── Video (atelier tour) — present but hidden by default ──
    [VIDEO]: {
      type: { resolvedName: "Video" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentVideo: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: true, textHeadline: td("wshVideoEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wshVideoHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("wshVideoText"), fontSizeText: "text-md", textAlignText: "center",
        buttonSize: "medium", buttonText: td("wshVideoButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "center",
        videoAspectRatio: "aspect-video", isVisibleVideo: true, videoUrl: "https://youtu.be/sdESKm-nKQE",
      },
      displayName: "Video", custom: { icon: "IconVideo", preview: pv("Video-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "wsh_vd_eb", header: "wsh_vd_hd", text: "wsh_vd_tx", button: "wsh_vd_btn" },
    },
    wsh_vd_eb: tc(VIDEO, { isVisible: true, text: td("wshVideoEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    wsh_vd_hd: tc(VIDEO, { isVisible: true, text: td("wshVideoHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    wsh_vd_tx: tc(VIDEO, { isVisible: true, text: td("wshVideoText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    wsh_vd_btn: btn(VIDEO, { text: td("wshVideoButton"), isVisible: true, displayName: "button" }),

    // ── Testimonials (dark surface) — hidden by default ──
    [TESTI]: {
      type: { resolvedName: "Testimonials" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextTestimonials: "gap-8 wb-md:gap-12", distTestimonials: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: true, gridColumns: "3", layoutWidth: "100%",
        backgroundColor: DARK,
        isVisibleHeadline: true, textHeadline: td("wshTestimonialsEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wshTestimonialsHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("wshTestimonialsHeader"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Testimonials", custom: { icon: "IconQuote", preview: pv("Testimonials-preview"), allowedComponents: ["TestimonialsChild"] },
      parent: B, hidden: false, nodes: ["wsh_ts1", "wsh_ts2", "wsh_ts3"],
      linkedNodes: { headline: "wsh_ts_eb", header: "wsh_ts_hd", text: "wsh_ts_tx" },
    },
    wsh_ts_eb: tc(TESTI, { isVisible: true, text: td("wshTestimonialsEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ON_DARK_ACCENT, header: true }),
    wsh_ts_hd: tc(TESTI, { isVisible: true, text: td("wshTestimonialsHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", color: "#ffffff", header: true }),
    wsh_ts_tx: tc(TESTI, { isVisible: false, text: td("wshTestimonialsHeader"), fontSize: "text-md", textAlign: "center", displayName: "text", color: ON_DARK, header: false }),
    wsh_ts1: testimonialChild(TESTI, 0, td),
    wsh_ts2: testimonialChild(TESTI, 1, td),
    wsh_ts3: testimonialChild(TESTI, 2, td),

    // ── Blog (pre-reader) — present but hidden by default ──
    [BLOG]: {
      type: { resolvedName: "Blog" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: true, textHeadline: td("wshBlogEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wshBlogHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("wshBlogText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Blog", custom: { icon: "IconClick", preview: pv("Blog-preview"), allowedComponents: ["BlogChild"] },
      parent: B, hidden: false, nodes: ["wsh_bl1", "wsh_bl2", "wsh_bl3"],
      linkedNodes: { headline: "wsh_bl_eb", header: "wsh_bl_hd", text: "wsh_bl_tx" },
    },
    wsh_bl_eb: tc(BLOG, { isVisible: true, text: td("wshBlogEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    wsh_bl_hd: tc(BLOG, { isVisible: true, text: td("wshBlogHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    wsh_bl_tx: tc(BLOG, { isVisible: true, text: td("wshBlogText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    wsh_bl1: blogChild(BLOG, 0, td),
    wsh_bl2: blogChild(BLOG, 1, td),
    wsh_bl3: blogChild(BLOG, 2, td),

    // ── FaQ ──
    [FAQ]: {
      type: { resolvedName: "FaQ" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: true, textHeadline: td("wshFaqEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wshFaqHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("wshFaqText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "FaQ", custom: { icon: "IconQuestionMark", preview: pv("faqPreview"), allowedComponents: ["FaQChild"] },
      parent: B, hidden: false, nodes: ["wsh_fq1", "wsh_fq2", "wsh_fq3", "wsh_fq4", "wsh_fq5", "wsh_fq6"],
      linkedNodes: { headline: "wsh_fq_eb", header: "wsh_fq_hd", text: "wsh_fq_tx" },
    },
    wsh_fq_eb: tc(FAQ, { isVisible: true, text: td("wshFaqEyebrow"), fontSize: "text-xs", textAlign: "center", displayName: "headline", color: ACCENT, header: true }),
    wsh_fq_hd: tc(FAQ, { isVisible: true, text: td("wshFaqHeader"), fontSize: "text-3xl", textAlign: "center", displayName: "header", header: true }),
    wsh_fq_tx: tc(FAQ, { isVisible: true, text: td("wshFaqText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    wsh_fq1: faqChild(FAQ, 0, td),
    wsh_fq2: faqChild(FAQ, 1, td),
    wsh_fq3: faqChild(FAQ, 2, td),
    wsh_fq4: faqChild(FAQ, 3, td),
    wsh_fq5: faqChild(FAQ, 4, td),
    wsh_fq6: faqChild(FAQ, 5, td),

    // ── Map (Atelier Berlin-Mitte) — present but hidden by default ──
    [MAP]: {
      type: { resolvedName: "Map" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentMap: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: true, textHeadline: td("wshMapEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("wshMapHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("wshMapText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("wshMapButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "left",
        mapRounded: "rounded-md",
        mapZoom: 14, mapType: "m", aspectRatio: "aspect-[4/3]", isVisibleMap: true, mapLocation: td("wshMapLocation"),
      },
      displayName: "Map", custom: { icon: "IconMap2", preview: pv("Map-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "wsh_mp_eb", header: "wsh_mp_hd", text: "wsh_mp_tx", button: "wsh_mp_btn" },
    },
    wsh_mp_eb: tc(MAP, { isVisible: true, text: td("wshMapEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    wsh_mp_hd: tc(MAP, { isVisible: true, text: td("wshMapHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    wsh_mp_tx: tc(MAP, { isVisible: true, text: td("wshMapText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    wsh_mp_btn: btn(MAP, { text: td("wshMapButton"), displayName: "button" }),

    // ── DataInput (multi-step RSVP) ──
    [DI]: {
      type: { resolvedName: "DataInput" }, isCanvas: false,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, textSpacing: DIST_CARD,
        backgroundColor: "transparent", border: false, borderWidth: "border-2", borderStyle: "border-solid",
        borderSides: ["bottom"], borderRadius: undefined,
        isVisibleHeadline: true, textHeadline: td("wshDataHeadline"), fontSizeHeadline: "text-base", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("wshDataHeader"), fontSizeHeader: "text-lg", textAlignHeader: "center",
        isVisibleText: true, textText: td("wshDataText"), fontSizeText: "text-sm", textAlignText: "center",
        allowUpdate: true,
        multiStepEnabled: true, stepLabels: [td("wshStepStatus"), td("wshStepProfile"), td("wshStepContext"), td("wshStepOverview")], multiStepMode: "pages",
        progressBarEnabled: true, progressBarColor: ACCENT,
        overviewEnabled: true, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []],
      },
      displayName: "DataInput", custom: { icon: "IconForms", preview: pv("DataInput-Preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "wsh_di_hl", header: "wsh_di_hd", text: "wsh_di_tx",
        fieldsGroup0: "wsh_di_f0", fieldsGroup1: "wsh_di_f1", fieldsGroup2: "wsh_di_f2",
        fieldsGroup3: "wsh_di_f3", fieldsGroup4: "wsh_di_f4",
        companionsGroup: "wsh_di_comp", overviewGroup: "wsh_di_ov", checkboxes: "wsh_di_cb",
        button: "wsh_di_btn", backButton: "wsh_di_back", nextButton: "wsh_di_next",
      },
    },
    wsh_di_hl: tc(DI, { isVisible: true, text: td("wshDataHeadline"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "center", displayName: "headline", header: true }),
    wsh_di_hd: tc(DI, { isVisible: true, text: td("wshDataHeader"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-lg", textAlign: "center", displayName: "header", header: true }),
    wsh_di_tx: tc(DI, { isVisible: true, text: td("wshDataText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "center", displayName: "text", header: false }),
    wsh_di_f0: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 4, groupIndex: 0, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["wsh_di_acceptance", "wsh_di_first", "wsh_di_last", "wsh_di_email"], linkedNodes: {},
    },
    // FIRST in the group, and filter-free — the question every other
    // field in this group is gated on. See `acceptanceFi`.
    wsh_di_acceptance: acceptanceFi("wsh_di_f0", td("acceptanceState")),
    wsh_di_first: fi("wsh_di_f0", { label: td("firstName"), requiredField: true, inputFieldVariable: "firstName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    wsh_di_last: fi("wsh_di_f0", { label: td("lastName"), requiredField: true, inputFieldVariable: "lastName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    wsh_di_email: fi("wsh_di_f0", { label: td("email"), requiredField: true, inputFieldVariable: "email", gridArea: "col-span-12", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    wsh_di_f1: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 2, groupIndex: 1, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["wsh_di_level", "wsh_di_role"], linkedNodes: {},
    },
    wsh_di_level: fi("wsh_di_f1", { label: td("wshFieldLevel"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "level", changeable: true }),
    wsh_di_role: fi("wsh_di_f1", { label: td("wshFieldRole"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "role", changeable: true }),
    wsh_di_f2: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 1, groupIndex: 2, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["wsh_di_expectation"], linkedNodes: {},
    },
    wsh_di_expectation: fi("wsh_di_f2", { label: td("wshFieldExpectation"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "expectation", multiline: true, gridArea: "col-span-12", changeable: true }),
    wsh_di_f3: emptyFieldsGroup(DI, 3),
    wsh_di_f4: emptyFieldsGroup(DI, 4),
    wsh_di_comp: {
      type: { resolvedName: "CompanionsGroup" }, isCanvas: true,
      props: { isVisible: false, fieldCount: 1, fieldSpacing: "medium", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER },
      displayName: "CompanionsGroup", custom: { icon: "IconUsers", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["wsh_di_comp_first"], linkedNodes: {},
    },
    wsh_di_comp_first: fi("wsh_di_comp", { label: td("firstName"), requiredField: false, inputFieldVariable: "firstName", gridArea: "col-span-12", changeable: true, type: "companion" }),
    wsh_di_ov_t: tc("wsh_di_ov", { isVisible: true, text: td("overviewTitle"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "left", displayName: "overviewTitle", header: true }),
    wsh_di_ov_x: tc("wsh_di_ov", { isVisible: true, text: td("overviewText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "left", displayName: "overviewText", header: false }),
    wsh_di_ov: {
      type: { resolvedName: "OverviewGroup" }, isCanvas: true,
      props: { isVisible: false, parentNodeId: DI, stepSections: [["fields-0"], ["fields-1"], ["fields-2"], []], stepLabels: [] },
      displayName: "summaryGroup", custom: { icon: "IconListDetails" },
      parent: DI, hidden: false, nodes: ["wsh_di_ov_t", "wsh_di_ov_x"], linkedNodes: {},
    },
    wsh_di_cb: {
      type: { resolvedName: "Checkboxes" }, isCanvas: false,
      props: { isVisible: true, checkboxes: toCheckboxItems(defaultRegistrationConsents(td)), displayName: "checkboxes" },
      displayName: "Checkboxes", custom: { icon: "IconCheckbox" },
      parent: DI, hidden: false, nodes: [], linkedNodes: {},
    },
    wsh_di_btn: btn(DI, { linkType: "internal", linkUrl: "", text: td("wshDataSubmit"), submittedText: "", isSubmitted: false, enableSubmittedState: true, size: "small", widthMode: "full", buttonType: "submit", margin: BTN_FORM_SP, displayName: "button" }),
    wsh_di_back: btn(DI, { linkType: "internal", type: "secondary", linkUrl: "", text: td("back"), navigationRole: "back", size: "small", widthMode: "content", isVisible: false, displayName: "backButton" }),
    wsh_di_next: btn(DI, { linkType: "internal", linkUrl: "", text: td("next"), navigationRole: "next", size: "small", widthMode: "content", isVisible: false, displayName: "nextButton" }),

    // ── Contact ──
    [CONTACT]: {
      type: { resolvedName: "Contact" }, isCanvas: true,
      props: {
        sectionHeight: 70, contentAlignment: "center", infoSection: true, backgroundColor: "transparent",
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3 wb-md:space-y-5 wb-lg:space-y-6", isVisible: true,
        infoWidth: "50%", inversed: false,
        isVisibleHeadline: true, textHeadline: td("wshContactEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("wshContactHeader"), fontSizeHeader: "text-3xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("wshContactText"), fontSizeText: "text-md", textAlignText: "left",
        isSocialVisible: true, socialIcons: [{ platform: "linkedin", url: "https://linkedin.com" }, { platform: "twitter", url: "https://twitter.com" }], socialSize: 24, socialPosition: "left",
        buttonSize: "medium", buttonText: td("wshContactButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
      },
      displayName: "Contact", custom: { icon: "IconForms", preview: pv("Contact-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "wsh_ct_eb", header: "wsh_ct_hd", text: "wsh_ct_tx",
        firstName: "wsh_ct_first", lastName: "wsh_ct_last", eMail: "wsh_ct_email",
        phoneNumber: "wsh_ct_phone", Message: "wsh_ct_msg", button: "wsh_ct_btn",
      },
    },
    wsh_ct_eb: tc(CONTACT, { isVisible: true, text: td("wshContactEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: ACCENT, header: true }),
    wsh_ct_hd: tc(CONTACT, { isVisible: true, text: td("wshContactHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", header: true }),
    wsh_ct_tx: tc(CONTACT, { isVisible: true, text: td("wshContactText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    wsh_ct_first: fi(CONTACT, { label: td("firstName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "firstName", changeable: false }),
    wsh_ct_last: fi(CONTACT, { label: td("lastName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "lastName", changeable: false }),
    wsh_ct_email: fi(CONTACT, { label: td("email"), overwriteType: "EMAIL", requiredField: true, inputFieldVariable: "email", changeable: false, locked: true }),
    wsh_ct_phone: fi(CONTACT, { label: td("phoneNumber"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "phone", changeable: false }),
    wsh_ct_msg: fi(CONTACT, { label: td("messageLabel"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "message", multiline: true, gridArea: "col-span-12", changeable: false, locked: true }),
    wsh_ct_btn: btn(CONTACT, { linkType: "internal", linkUrl: "", text: td("wshContactButton"), widthMode: "full", buttonType: "submit", displayName: "button", fixedNavigation: false }),
  });
};

// ── Meta sections (AppBar + Footer) ───────────────────────────────────

/** White bar, light rule, logo + nav + "Platz reservieren" CTA. */
export const workshopAppbar = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "AppBarSection" }, isCanvas: false,
    props: {
      padding: "p-4 wb-md:p-5 wb-lg:p-6", isVisible: true,
      backgroundColor: "#ffffff",
      border: true, borderColor: "#e8e8e8", borderWidth: "border", borderStyle: "border-solid", borderSides: ["bottom"],
      sectionOrder: ["logo", "links", "social"],
      isLogoVisible: true, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-10", logoPosition: "left",
      isLinksVisible: true, linksPosition: "center",
      links: [
        { label: td("wshLinkAgenda"), url: "#wsh_agenda", linkType: "internal" },
        { label: td("wshLinkInstructors"), url: "#wsh_speaker", linkType: "internal" },
        { label: td("wshLinkFaq"), url: "#wsh_faq", linkType: "internal" },
        { label: td("wshLinkContact"), url: "#wsh_contact", linkType: "internal" },
      ],
      linksColor: INK, linksFontSize: "text-sm", linksFontWeight: "font-medium",
      isSocialVisible: false, socialIcons: [], socialPosition: "right", socialSize: 24,
      logoutButtonSize: "small", logoutButtonText: td("wshHeroPrimaryButton"),
      logoutButtonBorder: true, logoutButtonBackgroundColor: ACCENT, logoutButtonColor: "#ffffff", logoutButtonBorderColor: ACCENT,
    },
    displayName: "AppBar", custom: { icon: "IconLayoutNavbar" },
    parent: "DCH9WIIT55", hidden: false, nodes: [], linkedNodes: {},
  });

/** Dark footer, accent top rule, four link groups + social + copyright. */
export const workshopFooter = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "FooterSection" }, isCanvas: false,
    props: {
      padding: "px-4 py-10 wb-md:px-6 wb-lg:px-8", isVisible: true,
      backgroundColor: DARK,
      border: true, borderColor: ACCENT, borderWidth: "border-4", borderStyle: "border-solid", borderSides: ["top"],
      // No footer logo (per design) — omit "logo" from sectionOrder + hide it.
      sectionOrder: ["links", "social"],
      isLogoVisible: false, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-auto",
      isLinkGroupsVisible: true,
      linkGroups: [
        { title: td("wshFooterProgram"), links: [
          { label: td("wshLinkAgenda"), url: "#wsh_agenda", linkType: "internal" },
          { label: td("wshLinkInstructors"), url: "#wsh_speaker", linkType: "internal" },
          { label: td("wshLinkAttendees"), url: "/login", linkType: "internal" },
        ] },
        // Event practicalities — Maps + add-to-calendar are EXTERNAL (open in a
        // new tab). URLs are placeholders the organiser edits per event.
        { title: td("wshFooterEvent"), links: [
          { label: td("wshLinkLocation"), url: "https://www.google.com/maps/search/?api=1&query=Auguststra%C3%9Fe+47+Berlin", linkType: "external" },
          { label: td("wshLinkCalendar"), url: "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Strategy+Workshop", linkType: "external" },
          { label: td("wshLinkTickets"), url: "/register", linkType: "internal" },
        ] },
        { title: td("wshFooterSupport"), links: [
          { label: td("wshLinkContact"), url: "#wsh_contact", linkType: "internal" },
          { label: td("wshLinkInhouse"), url: "#wsh_contact", linkType: "internal" },
          { label: td("wshLinkPress"), url: "#wsh_contact", linkType: "internal" },
        ] },
        { title: td("wshFooterLegal"), links: [
          { label: td("wshLinkPrivacy"), url: "/privacy", linkType: "internal" },
          { label: td("wshLinkImprint"), url: "/imprint", linkType: "internal" },
          { label: td("wshLinkCancellation"), url: "/terms", linkType: "internal" },
        ] },
      ],
      linksTitleColor: "#ffffff", linksTitleFontSize: "text-sm", linksTitleFontWeight: "font-semibold",
      linksColor: "#a3a3a3", linksFontSize: "text-sm", linksFontWeight: "font-normal", linksHoverColor: "hover:font-primary",
      isSocialVisible: true,
      socialIcons: [
        { altText: "linkedin", platform: "linkedin", url: "https://linkedin.com" },
        { altText: "twitter", platform: "twitter", url: "https://twitter.com" },
      ],
      socialColor: "#a3a3a3", socialHoverColor: "hover:font-primary", socialSize: 20, socialTitle: td("followUs"),
      isCopyrightVisible: true, copyrightColor: "#6b6b6b", copyrightFontSize: "text-xs", copyrightCompanyName: td("yourCompany"),
    },
    displayName: "Footer", custom: { icon: "IconMenu2" },
    parent: "xaUW2UDZT8", hidden: false, nodes: [], linkedNodes: {},
  });

// ── Theme CSS (P9 WS4-B4) ─────────────────────────────────────────────
//
// Marker comment first so a later user edit is distinguishable from the
// pristine preset. Composed from the shared premium-css builders (WS3).
// Strict minimal treatment: the hero stays a plain white sheet (node props);
// the css adds exactly one ink hairline under the headline block, a faint
// grid-dot ornament in the corner, uppercase letterspaced kickers, and the
// self-hosted faces so the preset's Work Sans/Inter stacks actually resolve.
// Motion is deliberately quiet — one fadeUp entrance + scroll-reveal, no
// ornament animation.
const CUSTOM_CSS = `/* theme:workshop */
${fontFaces([
  { family: "Work Sans", file: "work-sans" },
  { family: "Inter", file: "inter-regular", weightRange: "400" },
  { family: "Inter", file: "inter-bold", weightRange: "700" },
])}

/* Studio hero — clip + isolate the minimal ornament layers */
#wsh_hero {
${heroShell}
}

/* Tiny grid-dot ornament, top right — the only decoration the minimal
   direction allows itself (kept faint: ink dots on the white sheet) */
#wsh_hero::before {
${decorLayer}
  top: 9%;
  right: 6%;
  width: 132px;
  height: 84px;
  background-image: radial-gradient(circle, rgba(17, 17, 17, 0.28) 1px, transparent 1.5px);
  background-size: 12px 12px;
}

/* Single ink hairline beneath the headline block — the whole hero motif */
#wsh_hero::after {
${decorLayer}
  left: 6%;
  bottom: 12%;
  width: 120px;
  height: 1px;
  background: ${ACCENT};
}

/* Uppercase letterspaced kickers (the text-xs eyebrows in each section) */
#wsh_hero .text-xs, #wsh_numbers .text-xs, #wsh_agenda .text-xs, #wsh_speaker .text-xs,
#wsh_text .text-xs, #wsh_image .text-xs, #wsh_countdown .text-xs, #wsh_gallery .text-xs,
#wsh_video .text-xs, #wsh_testimonials .text-xs, #wsh_blog .text-xs, #wsh_faq .text-xs,
#wsh_map .text-xs, #wsh_contact .text-xs {
  text-transform: uppercase;
  letter-spacing: 0.22em;
}

${motionGate(`${fadeUpKeyframes("wsh", 14)}
  /* Quiet entrance: the sheet fades up once — no ornament animation */
  #wsh_hero { animation: wshFadeUp 0.7s ${EASE} both; }

${revealBlock("wsh", ["numbers", "datainput", "agenda", "speaker", "text", "image", "countdown", "gallery", "video", "testimonials", "blog", "faq", "map", "contact"], 30)}`)}
`;

// ── Style preset ──────────────────────────────────────────────────────
// "minimal-studio": `minimal` direction ink-on-white, Work Sans headings,
// Inter body, subtle rounded-md.
export const workshopPreset: WebsiteThemeStylePreset = {
  colorPalette: { primary: ACCENT, secondary: MUTE, background: SURFACE },
  fonts: { header: HEAD, body: SANS },
  rounding: "rounded-md",
  recommendedSize: "medium",
  bodyTheme: { fontColor: MUTE, fontSize: "16px", layoutOption: "full-width" },
  headingsTheme: {
    h1: { fontFamily: HEAD, fontSize: "44px", fontColor: INK },
    h2: { fontFamily: HEAD, fontSize: "26px", fontColor: INK },
  },
  buttonsTheme: {
    primary: {
      bgColor: ACCENT, bgHoverColor: "#000000", borderColor: ACCENT, borderHoverColor: "#000000",
      fontColor: "#FFFFFF", fontHoverColor: "#FFFFFF", fontSize: "16px", fontHoverSize: "16px",
      borderRadius: "rounded-md", hoverBorderRadius: "rounded-md",
    },
    secondary: {
      bgColor: "transparent", bgHoverColor: ACCENT, borderColor: ACCENT, borderHoverColor: ACCENT,
      fontColor: ACCENT, fontHoverColor: "#FFFFFF", fontSize: "16px", fontHoverSize: "16px",
      borderRadius: "rounded-md", hoverBorderRadius: "rounded-md",
    },
  },
  hyperlinkTheme: { fontColor: ACCENT, fontHoverColor: INK, underline: false },
  formInputTheme: {
    labelFontColor: MUTE, inputFontColor: INK, backgroundColor: "#FFFFFF",
    border: true, borderColor: "#E8E8E8", borderRadius: "rounded-md", borderStyle: "border-solid",
    borderWidth: "border", borderSides: [], fontFamily: SANS, fontSize: "text-sm", errorColor: "#DC2626",
  },
  heroImage: `${W}/hero-einladung.jpg`,
  logoSrc: LOGO,
  customCss: CUSTOM_CSS,
};
